import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FounderPill from "@/components/FounderPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { TimerIcon, TagIcon, SealCheckIcon, FlagIcon } from "@phosphor-icons/react";
import TrustTierBadge from "@/components/TrustTierBadge";
import FollowButton from "@/components/FollowButton";
import AuthenticatedBadge from "@/components/AuthenticatedBadge";
import PlusRedeemBox from "@/components/PlusRedeemBox";
import SeoHead, { productLd, breadcrumbLd } from "@/components/SeoHead";
import BNPLBadge from "@/components/BNPLBadge";
import ReportDialog from "@/components/ReportDialog";
import OwnerListingActions from "@/components/OwnerListingActions";
import ComposePostModal from "@/components/ComposePostModal";

const fileUrl = (path) =>
  path ? `${SITE_ORIGIN}/api/files/${path}` : null;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [bids, setBids] = useState([]);
  const [bid, setBid] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [redeemCents, setRedeemCents] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [composePostOpen, setComposePostOpen] = useState(false);
  // Default-card surfacing — enables the one-tap "Pay with •••• 1234"
  // button so we don't punt the buyer to Stripe Checkout every time.
  const [defaultCard, setDefaultCard] = useState(null);

  const load = async () => {
    try {
      const [l, b, pp] = await Promise.all([
        api.get(`/listings/${id}`),
        api.get(`/listings/${id}/bids`),
        api.get("/payments/paypal/config").catch(() => ({ data: { enabled: false } })),
      ]);
      setListing(l.data);
      setBids(b.data.items);
      setPaypalEnabled(!!pp.data.enabled);
    } catch {
      toast.error("Listing not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  // Pull the user's default saved card (if any) so we can offer the
  // one-tap "Pay with •••• 1234" button in place of the redirect-to-Stripe
  // checkout. Soft-fails silently — buyers without a card just see the
  // regular Buy / Checkout flow.
  useEffect(() => {
    if (!user) {
      setDefaultCard(null);
      return;
    }
    let alive = true;
    api.get("/me/wallet/payment-methods")
      .then(({ data }) => {
        if (!alive) return;
        const def = (data.cards || []).find((c) => c.is_default);
        setDefaultCard(def || null);
      })
      .catch(() => { if (alive) setDefaultCard(null); });
    return () => { alive = false; };
  }, [user]);

  const placeBid = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }
    const amount = parseFloat(bid);
    if (!amount || amount <= 0) return;
    setBusy(true);
    try {
      await api.post(`/listings/${id}/bid`, { amount });
      toast.success(`Bid placed: $${amount.toFixed(2)}`);
      setBid("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const buyNow = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        listing_id: id,
        origin_url: window.location.origin,
        credits_to_redeem_cents: redeemCents,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  // One-tap instant checkout — charges the user's default saved card via
  // /api/payments/instant-checkout (PaymentIntent w/ off_session=true).
  // Falls back gracefully:
  //   • If Stripe responds with `requires_action` (3DS), we use stripe.js
  //     to confirm the card payment, then poll /instant-checkout/status
  //     to finalize the order before navigating to /order/{order_id}.
  //   • On any non-recoverable failure we surface the friendly error.
  const buyWithSavedCard = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/instant-checkout", {
        listing_id: id,
        origin_url: window.location.origin,
        credits_to_redeem_cents: redeemCents,
      });

      if (data.status === "succeeded" && data.order_id) {
        toast.success("Payment confirmed");
        navigate(`/order/${data.order_id}`);
        return;
      }

      if (data.status === "requires_action" && data.client_secret) {
        // 3D Secure / SCA challenge — let Stripe.js render the bank's
        // modal, then poll backend to finalize.
        const stripe = await loadStripe(data.publishable_key);
        if (!stripe) {
          toast.error("Could not load Stripe. Try again.");
          return;
        }
        const { error: scaErr } = await stripe.confirmCardPayment(data.client_secret);
        if (scaErr) {
          toast.error(scaErr.message || "Authentication failed.");
          return;
        }
        const { data: status } = await api.get(`/payments/instant-checkout/status/${data.pi_id}`);
        if (status.status === "succeeded" && status.order_id) {
          toast.success("Payment confirmed");
          navigate(`/order/${status.order_id}`);
        } else {
          toast.error(`Payment did not complete (${status.status}).`);
        }
        return;
      }

      toast.error("Payment could not complete. Try a different card.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const payWithPayPal = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }
    setBusy(true);
    try {
      // check if PayPal enabled
      const cfg = await api.get("/payments/paypal/config");
      if (!cfg.data.enabled) {
        toast.error("PayPal is not configured yet. Please use Stripe.");
        return;
      }
      const { data } = await api.post("/payments/paypal/create-order", {
        listing_id: id,
        origin_url: window.location.origin,
        credits_to_redeem_cents: redeemCents,
      });
      if (!data.approve_url) {
        toast.error("PayPal did not return an approve URL. Please try Stripe.");
        return;
      }
      window.location.href = data.approve_url;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen"><Navbar /><div className="max-w-7xl mx-auto p-10">Loading…</div></div>
  );
  if (!listing) return null;

  const segmentMismatch =
    user && user.segment && user.segment !== listing.segment;

  const minBid =
    (listing.current_bid || listing.starting_bid || 0) + (listing.highest_bidder_id ? 1 : 0);
  const mainImg = listing.images?.[0] ? fileUrl(listing.images[0]) : null;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`listing-page-${listing.id}`}>
      <SeoHead
        title={`${listing.title} — ${listing.type === "auction" ? "Live auction" : "Buy on"} Bidorah`}
        description={
          (listing.description || listing.title) +
          ` · From a US-verified ${listing.segment === "wholesale" ? "wholesale" : ""} seller on Bidorah · $${Number(listing.price || listing.current_bid || 0).toFixed(2)}`
        }
        canonical={`https://bidorah.com/listing/${listing.id}`}
        image={(listing.images || [])[0] || "https://bidorah.com/icon-512.png"}
        type="product"
        keywords={`${listing.title}, ${listing.category}, bidorah, ${listing.type === "auction" ? "live auction" : "buy it now"}, verified seller`}
        jsonLd={[
          productLd(listing),
          breadcrumbLd([
            { name: "Home", url: "https://bidorah.com/" },
            { name: "Browse", url: "https://bidorah.com/browse" },
            { name: listing.category, url: `https://bidorah.com/category/${encodeURIComponent(listing.category || "")}` },
            { name: listing.title, url: `https://bidorah.com/listing/${listing.id}` },
          ]),
        ]}
      />
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square bg-zinc-100 rounded-2xl relative overflow-hidden shadow-sm">
            {mainImg ? (
              <img src={mainImg} alt={listing.title} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full grid place-items-center text-zinc-400 font-mono text-xs">
                {listing.category}
              </div>
            )}
            <div className="absolute top-3 left-3 flex gap-1.5">
              <span
                className={`pill ${
                  listing.segment === "wholesale"
                    ? "bg-amber-500 text-white"
                    : "bg-[#1D78AB] text-white"
                }`}
              >
                {listing.segment}
              </span>
              <span className="pill pill-dark">
                {listing.type === "auction" ? <><TimerIcon size={10} /> AUCTION</> : <><TagIcon size={10} /> BUY NOW</>}
              </span>
            </div>
          </div>
          {listing.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {listing.images.slice(1, 6).map((p, i) => (
                <img
                  key={i}
                  src={fileUrl(p)}
                  alt=""
                  className="aspect-square object-cover rounded-xl border border-zinc-200"
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="overline text-zinc-500">{listing.category}</p>
          <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-2">
            {listing.title}
          </h1>
          {user && (user.user_id === listing.seller_id || user.role === "admin") && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <OwnerListingActions
                listing={listing}
                onChange={(fresh) => setListing(fresh)}
              />
              {user.user_id === listing.seller_id && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setComposePostOpen(true)}
                  className="text-[#1D78AB] border-[#1D78AB]/40 hover:bg-[#1D78AB] hover:text-white"
                  data-testid="listing-share-to-feed-btn"
                >
                  Share to feed
                </Button>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-zinc-600">
            <SealCheckIcon size={16} className="text-[#1D78AB]" />
            Seller{" "}
            <Link
              to={`/seller/${listing.seller_id}`}
              className="font-semibold hover:text-[#1D78AB]"
              data-testid="seller-link"
            >
              {listing.seller_name}
            </Link>
            {listing.seller_trust_tier && (
              <TrustTierBadge tier={listing.seller_trust_tier} size="sm" />
            )}
            <FollowButton sellerId={listing.seller_id} size="sm" />
            {user && user.user_id !== listing.seller_id && (
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-700 hover:underline ml-1"
                data-testid="listing-report-btn"
                title="Report this listing"
              >
                <FlagIcon size={12} /> Report
              </button>
            )}
          </div>

          <div className="mt-6 card-modern p-6">
            <AuthenticatedBadge listingId={listing.id} />
            {listing.type === "auction" ? (
              <div>
                <p className="overline text-zinc-500">
                  {listing.status === "ended" ? "WINNING BID" : "CURRENT BID"}
                </p>
                <div className="font-display font-black text-4xl text-[#1D78AB] mt-1 font-mono">
                  ${(listing.current_bid || listing.starting_bid || 0).toFixed(2)}
                </div>
                <p className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                  {listing.bid_count} bids · {listing.highest_bidder_name || "No bidders yet"}
                  {listing.highest_bidder_is_founder && (
                    <FounderPill is_founder size="xs" testid="bid-founder-pill" />
                  )}
                </p>
                {listing.auction_ends_at && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {listing.status === "ended" ? "Ended" : "Ends"}{" "}
                    {new Date(listing.auction_ends_at).toLocaleString()}
                  </p>
                )}
                <ShippingChip estimate={listing.shipping_estimate} />

                {segmentMismatch ? (
                  <div className="mt-4 p-3 border border-red-300 bg-red-50 rounded-xl text-sm">
                    You're a <b>{user.segment}</b> user. This is a <b>{listing.segment}</b> listing.
                    Wholesale & retail cannot mix.
                  </div>
                ) : listing.status === "ended" &&
                  user &&
                  user.user_id === listing.highest_bidder_id ? (
                  <div
                    className="mt-4 p-4 border border-emerald-300 bg-emerald-50 rounded-2xl"
                    data-testid="auction-won-cta"
                  >
                    <p className="font-display font-bold text-emerald-800 text-base">
                      You won this auction.
                    </p>
                    <p className="text-xs text-emerald-700 mb-3">
                      Complete checkout to pay the seller and lock in shipping.
                    </p>
                    <PlusRedeemBox
                      user={user}
                      priceUsd={Number(listing.current_bid || listing.starting_bid || listing.price || 0)}
                      value={redeemCents}
                      onChange={setRedeemCents}
                    />
                    <div className="space-y-2 mt-3">
                      {defaultCard && (
                        <Button
                          onClick={buyWithSavedCard}
                          disabled={busy}
                          className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white h-11 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20"
                          data-testid="auction-checkout-saved-card-btn"
                        >
                          {busy
                            ? "Charging…"
                            : `Pay with ${defaultCard.brand.toUpperCase()} •••• ${defaultCard.last4}`}
                        </Button>
                      )}
                      <Button
                        onClick={buyNow}
                        disabled={busy}
                        className={`w-full h-11 rounded-full font-semibold shadow-lg ${
                          defaultCard
                            ? "bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 shadow-none"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20"
                        }`}
                        data-testid="auction-checkout-stripe-btn"
                      >
                        {busy
                          ? "Redirecting…"
                          : defaultCard
                          ? "Use a different payment method"
                          : "Complete checkout with Stripe"}
                      </Button>
                      {paypalEnabled && (
                        <Button
                          onClick={payWithPayPal}
                          disabled={busy}
                          variant="outline"
                          className="w-full h-11 rounded-full border-[#ffc439] text-zinc-900 hover:bg-[#ffc439]/20 font-semibold"
                          data-testid="auction-checkout-paypal-btn"
                        >
                          Pay with PayPal
                        </Button>
                      )}
                    </div>
                  </div>
                ) : listing.status === "ended" ? (
                  <p className="mt-4 text-sm text-zinc-500 inline-flex items-center gap-1">
                    Auction closed. {listing.highest_bidder_name} won this lot.
                    {listing.highest_bidder_is_founder && (
                      <FounderPill is_founder size="xs" />
                    )}
                  </p>
                ) : listing.status !== "active" ? (
                  <p className="mt-4 text-sm text-zinc-500">Auction closed.</p>
                ) : (
                  <div className="mt-4 flex gap-2">
                    <Input
                      type="number"
                      min={minBid}
                      step="1"
                      value={bid}
                      onChange={(e) => setBid(e.target.value)}
                      placeholder={`Min $${minBid.toFixed(2)}`}
                      className="flex-1 rounded-full h-12 px-5"
                      data-testid="bid-amount-input"
                    />
                    <Button
                      onClick={placeBid}
                      disabled={busy}
                      className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-12 px-6 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20"
                      data-testid="place-bid-btn"
                    >
                      Place bid
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="overline text-zinc-500">PRICE</p>
                <div className="font-display font-black text-4xl text-[#1D78AB] mt-1 font-mono">
                  ${Number(listing.price).toFixed(2)}
                </div>
                <ShippingChip estimate={listing.shipping_estimate} />
                <BNPLBadge
                  amount={Number(listing.price || 0)}
                  onCheckout={buyNow}
                  disabled={busy || segmentMismatch}
                />
                {segmentMismatch ? (
                  <div className="mt-4 p-3 border border-red-300 bg-red-50 text-sm">
                    You're a <b>{user.segment}</b> user. This is a <b>{listing.segment}</b> listing.
                    Wholesale & retail cannot mix.
                  </div>
                ) : listing.status !== "active" ? (
                  <p className="mt-4 text-sm text-zinc-500">No longer available.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    <PlusRedeemBox
                      user={user}
                      priceUsd={Number(listing.price || 0)}
                      value={redeemCents}
                      onChange={setRedeemCents}
                    />
                    {defaultCard && (
                      <Button
                        onClick={buyWithSavedCard}
                        disabled={busy}
                        className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white h-12 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20 hover:shadow-xl hover:shadow-[#1D78AB]/30"
                        data-testid="buy-now-saved-card-btn"
                      >
                        {busy
                          ? "Charging…"
                          : `Pay with ${defaultCard.brand.toUpperCase()} •••• ${defaultCard.last4}`}
                      </Button>
                    )}
                    <Button
                      onClick={buyNow}
                      disabled={busy}
                      className={`w-full h-12 rounded-full font-semibold shadow-lg ${
                        defaultCard
                          ? "bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 shadow-none"
                          : "bg-[#1D78AB] hover:bg-[#165d85] text-white shadow-[#1D78AB]/20 hover:shadow-xl hover:shadow-[#1D78AB]/30"
                      }`}
                      data-testid="buy-now-btn"
                    >
                      {busy
                        ? "Redirecting..."
                        : defaultCard
                        ? "Use a different payment method"
                        : "Buy now with Stripe"}
                    </Button>
                    {paypalEnabled && (
                      <Button
                        onClick={payWithPayPal}
                        disabled={busy}
                        variant="outline"
                        className="w-full h-12 rounded-full border-[#ffc439] text-zinc-900 hover:bg-[#ffc439]/20 font-semibold"
                        data-testid="buy-paypal-btn"
                      >
                        Pay with PayPal
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6">
            <p className="overline text-zinc-500 mb-2">DESCRIPTION</p>
            <p className="text-sm text-zinc-700 whitespace-pre-line">{listing.description}</p>
          </div>

          {listing.type === "auction" && bids.length > 0 && (
            <div className="mt-8">
              <p className="overline text-zinc-500 mb-2">BID HISTORY</p>
              <div className="card-modern divide-y divide-zinc-100">
                {bids.slice(0, 10).map((b) => (
                  <div key={b.id} className="p-3 flex items-center justify-between text-sm">
                    <span className="font-semibold">{b.user_name}</span>
                    <span className="font-mono text-[#1D78AB] font-bold">
                      ${b.amount.toFixed(2)}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {new Date(b.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="listing"
        targetId={listing.id}
        targetLabel={listing.title}
      />
      <ComposePostModal
        open={composePostOpen}
        onOpenChange={setComposePostOpen}
        prefilledListingId={listing.id}
        prefilledListing={listing}
      />
      <Footer />
    </div>
  );
}


/**
 * Tiny "Ships for $X.XX" chip rendered just below the listing price.
 * Renders nothing when the seller didn't set a flat shipping estimate.
 * Cuts cart-abandonment by showing total cost up front (a top-3 reason
 * shoppers bail at checkout).
 */
function ShippingChip({ estimate }) {
  if (!Number.isFinite(estimate) || estimate <= 0) return null;
  return (
    <div
      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold px-3 py-1"
      data-testid="shipping-chip"
      title="Flat shipping estimate set by the seller. Taxes calculated at checkout."
    >
      <span aria-hidden>📦</span>
      Ships for ${estimate.toFixed(2)}
    </div>
  );
}
